import { uiText } from '../../i18n/uiText';
import React, { useEffect, useMemo, useState } from 'react';
import { BookOpen, Check, FileText, Search, Sparkles, X } from 'lucide-react';
import Dialog from '../common/Dialog';
import { getApiErrorMessage, localAiApi } from '../../api';

const SOURCE_LABELS = {
  upload: uiText("Личный документ"),
  audit_file: uiText("Файл аудита"),
  audit_record: uiText("Данные аудита"),
  system_document: uiText("Документ Audit Pro"),
};

export default function ExpectedAnswerModal({ question, defaultDocumentId, onClose, onGenerate, generating }) {
  const [documents, setDocuments] = useState([]);
  const [selectedId, setSelectedId] = useState(defaultDocumentId ? String(defaultDocumentId) : '');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    localAiApi.documents()
      .then((response) => {
        if (!alive) return;
        const ready = (response.data || []).filter((item) => item.status === 'ready' && Number(item.chunks_count || 0) > 0);
        setDocuments(ready);
        if (defaultDocumentId && ready.some((item) => String(item.id) === String(defaultDocumentId))) {
          setSelectedId(String(defaultDocumentId));
        }
      })
      .catch((requestError) => alive && setError(getApiErrorMessage(requestError)))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [defaultDocumentId]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase('ru-RU');
    if (!needle) return documents;
    return documents.filter((item) => [item.title, item.original_name, SOURCE_LABELS[item.source_kind]]
      .filter(Boolean)
      .join(' ')
      .toLocaleLowerCase('ru-RU')
      .includes(needle));
  }, [documents, search]);

  return (
    <Dialog size="lg" labelledBy="expected-answer-title" onClose={onClose} closeDisabled={generating}>
          <div className="ap-dialog__header">
            <div>
              <div className="ap-dialog__title" id="expected-answer-title" data-dialog-heading tabIndex={-1}><Sparkles size={18} />{uiText(" Сгенерировать ожидаемый ответ")}</div>
              <div className="expected-answer-modal-subtitle">{uiText("ИИ использует только выбранный документ и текст текущего вопроса.")}</div>
            </div>
            <button type="button" className="ap-dialog__close" onClick={onClose} disabled={generating} aria-label={uiText("Закрыть")}><X size={17} /></button>
          </div>

          <div className="ap-dialog__body ap-stack">
            <div className="expected-answer-question-preview">
              <span>{uiText("Вопрос чек-листа")}</span>
              <strong>{question?.text}</strong>
            </div>

            <label className="expected-answer-search">
              <Search size={17} />
              <input className="ap-control" aria-label={uiText("Поиск документа")} value={search} onChange={(event) => setSearch(event.target.value)} placeholder={uiText("Поиск по названию документа или типу")} />
            </label>

            {error ? <div className="alert alert-error">{error}</div> : null}
            {loading ? <div className="expected-answer-empty">{uiText("Загружаем доступную базу документов…")}</div> : null}
            {!loading && !filtered.length ? (
              <div className="expected-answer-empty">
                <BookOpen size={28} />
                <strong>{documents.length ? uiText("Ничего не найдено") : uiText("Нет готовых документов")}</strong>
                <span>{documents.length ? uiText("Измените поисковый запрос.") : uiText("Сначала загрузите или дождитесь индексации документа в ИИ-ассистенте.")}</span>
              </div>
            ) : null}
            {!loading && filtered.length ? (
              <div className="expected-answer-document-list" role="radiogroup" aria-label={uiText("Документ-источник")}>
                {filtered.map((document) => {
                  const selected = String(document.id) === selectedId;
                  return (
                    <button
                      type="button"
                      key={document.id}
                      className={`expected-answer-document${selected ? ' is-selected' : ''}`}
                      onClick={() => setSelectedId(String(document.id))}
                      role="radio"
                      aria-checked={selected}
                    >
                      <span className="expected-answer-document-icon"><FileText size={18} /></span>
                      <span className="expected-answer-document-copy">
                        <strong>{document.title}</strong>
                        <small>{SOURCE_LABELS[document.source_kind] || uiText("Документ")} · {document.chunks_count}{uiText(" фрагм.")}</small>
                      </span>
                      <span className="expected-answer-document-check">{selected ? <Check size={15} /> : null}</span>
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>

          <div className="ap-dialog__footer">
            <span className="expected-answer-privacy-note">{uiText("Личные загрузки других аудиторов здесь не отображаются.")}</span>
            <div className="expected-answer-modal-actions">
              <button type="button" className="ap-button ap-button--secondary" onClick={onClose} disabled={generating}>{uiText("Отмена")}</button>
              <button type="button" className="ap-button ap-button--primary" disabled={!selectedId || generating} onClick={() => onGenerate(Number(selectedId))}>
                <Sparkles size={15} /> {generating ? uiText("ИИ анализирует документ…") : uiText("Сгенерировать")}
              </button>
            </div>
          </div>
    </Dialog>
  );
}
